// Predicates for the credit machine-error envelope (contract 11 §6.3):
//   { error: { code, message, user_visible, details } }
//
// Depletion is HTTP **402 CREDIT_DEPLETED** (out of credits) and is distinct from **403
// PACKAGE_FORBIDDEN** ("feature not in your plan"); it is never 401. The 402 `details` carries a
// full `BalancePayload`, so `useCreditGuard` (ORP-18684) can hydrate the store from the error with
// no extra call.
//
// v4 change: these read module 03's `ApiError`, not a raw axios shape. `ApiError.code` is already
// nested-aware (`ApiError.ts` `extractCode` reads `body.error.code` first), so the code check needs
// no branch. `details` has NO first-class field on `ApiError` — contract §18 claims module 03
// surfaces it, but the shipped class does not; §6.3 describes the actual seam, so `details` is read
// off `ApiError.payload`, which exists for exactly this ("raw response body, for feature-specific
// reads"). Extending the shared class for one feature would be a module-03 contract change.
//
// Pure: no store, no Vue, no axios. The api class already sends every credit route with
// `suppressGlobalErrorHandler`, so these statuses reach the feature instead of a global toast.
import { CREDIT_ERROR_CODES } from '../models/credit.types'
import type { BalancePayload, CreditErrorEnvelope } from '../models/credit.types'

import { ApiError } from '@/shared/http'

/**
 * i18n key the caller renders when the backend sends no user-visible message — replaces v3's
 * hardcoded `"You're out of AI credits."` default argument (`creditError.ts:45`).
 */
export const CREDIT_ERROR_FALLBACK_KEY = 'credits.depleted.title'

/** The `{ error: … }` envelope carried on the raw body, or null when the error is not one. */
function errorEnvelope(error: unknown): CreditErrorEnvelope['error'] | null {
  if (!ApiError.isApiError(error)) return null
  const body = error.payload
  if (!body || typeof body !== 'object' || !('error' in body)) return null
  const inner = (body as CreditErrorEnvelope).error
  return inner && typeof inner === 'object' ? inner : null
}

/** True when the action was blocked for being out of credits (402 CREDIT_DEPLETED). */
export function isCreditDepletedError(error: unknown): boolean {
  return (
    ApiError.isApiError(error) && error.status === 402 && error.code === CREDIT_ERROR_CODES.DEPLETED
  )
}

/** True when the feature is not in the account's plan (403 PACKAGE_FORBIDDEN). */
export function isPackageForbiddenError(error: unknown): boolean {
  return (
    ApiError.isApiError(error) &&
    error.status === 403 &&
    error.code === CREDIT_ERROR_CODES.FORBIDDEN
  )
}

/** The full `BalancePayload` embedded in a 402 CREDIT_DEPLETED error, or null. */
export function creditDepletedDetails(error: unknown): BalancePayload | null {
  if (!isCreditDepletedError(error)) return null
  return (errorEnvelope(error)?.details as BalancePayload | undefined) ?? null
}

/**
 * The backend's user-visible message from the error envelope, or `null` when there is none.
 *
 * v3 returned an English literal in the `null` case; a pure service owns no copy, so the caller
 * renders {@link CREDIT_ERROR_FALLBACK_KEY} instead: `creditErrorMessage(e) ?? t(CREDIT_ERROR_FALLBACK_KEY)`.
 */
export function creditErrorMessage(error: unknown): string | null {
  return errorEnvelope(error)?.message || null
}
